import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { getStaffInfo } from "../api-services/brokers.ts";
import { getTenantInfo } from "../api-services/agent-platform-tenant";
import { BrokerInfo, StaffRole, TenantInfoResponse } from "../types/livechat.types";

interface UseLivechatWidgetOptions {
  agencyId: string | undefined;
  staffCode: string | undefined;
  isLoggedIn: boolean;
}

export const useLivechatWidget = ({
  agencyId,
  staffCode,
  isLoggedIn,
}: UseLivechatWidgetOptions) => {
  const scriptInjectedRef = useRef(false);
  const livechatUrl = process.env.GATSBY_AGENT_LIVECHAT_URL;

  const { data: brokerData } = useQuery<BrokerInfo>(
    ["livechatBrokerInfo", staffCode],
    () => getStaffInfo(staffCode!),
    {
      enabled: isLoggedIn && !!staffCode && !!livechatUrl,
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const staffId = useMemo(() => {
    if (!brokerData?.staffRoles || !agencyId) return undefined;
    const matchingRole = brokerData.staffRoles.find(
      (role: StaffRole) => role.companyId === agencyId
    );
    return matchingRole?.staffId;
  }, [brokerData, agencyId]);

  const { data: tenantData } = useQuery<TenantInfoResponse>(
    ["livechatTenantInfo", agencyId, staffId],
    () => getTenantInfo(agencyId!, staffId),
    {
      enabled: isLoggedIn && !!agencyId && !!livechatUrl,
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const tenantId = tenantData?.data?.tenantId ?? null;
  const customerSupportID = tenantData?.data?.customerSupportID ?? null;

  useEffect(() => {
    if (!isLoggedIn || !livechatUrl || !tenantId || scriptInjectedRef.current) {
      return;
    }
    if (document.getElementById("rocketchat-livechat-widget")) {
      scriptInjectedRef.current = true;
      return;
    }

    (window as any).RocketChat = function (c: unknown) {
      (window as any).RocketChat._.push(c);
    };
    (window as any).RocketChat._ = [];
    (window as any).RocketChat.url = `${livechatUrl}/livechat`;
    (window as any).ticketplus = (window as any).ticketplus || {};
    (window as any).ticketplus.tenantid = tenantId;
    if (customerSupportID) {
      (window as any).ticketplus.csid = customerSupportID;
    }

    const script = document.createElement("script");
    script.id = "rocketchat-livechat-widget";
    script.async = true;
    script.src = `${livechatUrl}/livechat/rocketchat-livechat.min.js?_=1`;
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode!.insertBefore(script, firstScript);
    scriptInjectedRef.current = true;

    return () => {
      document.getElementById("rocketchat-livechat-widget")?.remove();
      delete (window as any).RocketChat;
      delete (window as any).ticketplus;
      scriptInjectedRef.current = false;
    };
  }, [isLoggedIn, livechatUrl, tenantId, customerSupportID]);
};
